import React, { useCallback, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRange, DateTimeRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { withTranslation, WithTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { Icon } from '@components/Outputs';
import { IconButton } from '@mui/material';
import { toDayjsLocale } from '@utils/locales/locale.utils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DateTimeRangeInputProps extends WithTranslation {
  disabled?: boolean;
  isReadOnly?: boolean;
  label1?: string;
  label2?: string;
  timezone?: string;
  value: Array<string | Date | undefined>;
  onChange?: (dateTime: Array<string | Date | undefined>) => void;
}

const DateTimeRangeInput: React.FC<DateTimeRangeInputProps> = props => {
  const t = (key: string | undefined) => key && props.t(key).toString();
  const lng = props.i18n.language;
  const dayjsLocale = toDayjsLocale(lng);

  dayjs.locale(dayjsLocale);
  const [open, setOpen] = useState(false);

  const dateTimeValue: [Dayjs | null, Dayjs | null] = useMemo(
    () => [props.value[0] ? dayjs(props.value[0]) : null, props.value[1] ? dayjs(props.value[1]) : null],
    [props.value]
  );

  const handleChangeValue = debounce((value: DateRange<Dayjs>) => {
    if (typeof props.onChange === 'function') {
      const startDate = value[0];
      const endDate = value[1];

      const fixedEndDate = endDate && startDate && endDate.isBefore(startDate) ? undefined : endDate;

      if (startDate?.isValid() || fixedEndDate?.isValid())
        props.onChange([startDate?.toISOString(), fixedEndDate?.toISOString()]);
    }
  }, 500);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
      <DateTimeRangePicker
        localeText={{ start: t(props.label1 ?? 'From'), end: t(props.label2 ?? 'To') }}
        value={dateTimeValue}
        onChange={handleChangeValue}
        disabled={props.disabled}
        readOnly={props.isReadOnly}
        ampm={false}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            variant: 'standard',
            InputProps: {
              endAdornment: (
                <IconButton onClick={() => setOpen(true)} sx={{ padding: 0 }}>
                  <Icon icon="event" />
                </IconButton>
              ),
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default withTranslation()(DateTimeRangeInput);
